<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import moment from 'moment'
import { ref, computed, watch, onMounted, onBeforeUnmount, useTemplateRef, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from "@kyvg/vue3-notification"
import CreateVmixTokenModal from '@/components/modals/CreateVmixTokenModal.vue'
import EditVmixTokenModal from '@/components/modals/EditVmixTokenModal.vue'
import DeleteVmixTokenModal from '@/components/modals/DeleteVmixTokenModal.vue'
import VmixTokenService from '@/services/VmixTokenService'
import { APP_EVENTS, onAppEvent } from '@/Utils/appEventBus'
import FormButton from '../../components/Atoms/FormButton.vue'
import Tooltip from '../../components/Atoms/Tooltip.vue'
import IconBase from '../../components/icon/IconBase.vue'
import IconBin from '../../components/icon/IconBin.vue'

const instance = getCurrentInstance()
const root = instance.proxy.$root
const { notify } = useNotification()
const store = useStore()

// Refs
const error = ref(null)
const processing = ref(false)
const vmixTokens = ref([])
const vmixToken = ref({})
const fields = ref([
  { key: 'name', label: 'Token Name', class: 'text-s-s' },
  { key: 'createdAt', label: 'Created', class: 'text-s-s' },
  { key: 'actions', label: '' }
])

// Computed
const loadingStatus = computed(() => store.getters['Ui/loadingStatus'])
const canAccessDevelopers = computed(() => store.getters['User/canAccessDevelopers'])
const userEmail = computed(() => store.getters['User/userEmail'])

// Hooks
onMounted(() => {
  // track event
  window.trackEvent('Page Viewed', { page: 'vMix Token Settings' })
})

// The token belongs to the selected account. Drop the previous account's list
// as soon as a switch starts. Reload when the switch settles: MainSidebar
// clears Ui/loadingStatus even when TEAM_CHANGED is skipped (a switch that
// stops early), and `activatedTeam` is the account the list belongs to once
// loadUser has caught up with the new session.
const accountSwitching = ref(false)
const selectedAccountId = computed(() => store.state.User.activatedTeam || null)
// Bumped on every load and on account switch, so a response for the previous
// account can't overwrite the current one.
let loadRequestId = 0

const dropTokenList = () => {
  loadRequestId++
  vmixTokens.value = []
  processing.value = true
}

onBeforeUnmount(onAppEvent(APP_EVENTS.TEAM_CHANGING, () => {
  accountSwitching.value = true
  dropTokenList()
}))
watch(loadingStatus, (loading) => {
  if (loading || !accountSwitching.value) return
  accountSwitching.value = false
  loadVmixTokens()
})
watch(selectedAccountId, (next, prev) => {
  if (next === prev) return
  dropTokenList()
  if (!canAccessDevelopers.value) {
    processing.value = false
    return
  }
  loadVmixTokens()
})

const loadVmixTokens = async () => {
  const requestId = ++loadRequestId
  processing.value = true
  let tokens = []
  try {
    const res = await VmixTokenService.getVmixTokens()
    tokens = Array.isArray(res) ? res : (res ? [res] : [])
  } catch (e) {
    if (requestId !== loadRequestId) return
    notify({ group: 'error', text: `Unable to retrieve vMix tokens.` })
  }
  if (requestId !== loadRequestId) return
  vmixTokens.value = tokens
  processing.value = false
}

const createToken = () => {
  showModal('modalCreateVmixToken')
}

const editVmixToken = (token) => {
  vmixToken.value = token
  showModal('modalEditVmixToken')
}

const deleteVmixToken = (token) => {
  vmixToken.value = token
  showModal('modalDeleteVmixToken')
}

const onTokenDismiss = () => {
  vmixToken.value = {}
}

const onTokenUpdate = async () => {
  await loadVmixTokens()
}

const formatDate = (dateValue) => {
  if (!dateValue) return ''
  return moment(new Date(String(dateValue))).format('MM/DD/YYYY, h:mm A')
}


// Member permissions can resolve after mount, so load once access is granted.
watch(canAccessDevelopers, (allowed) => {
  if (allowed) loadVmixTokens()
}, { immediate: true })
</script>

<template>
  <div class="view">
    <main>
      <section class="text-center mt-6 lg:mt-12" v-if="!canAccessDevelopers">
        <h4 class="text-surface-9 text-lg font-medium">You don’t have permission to access this.</h4>
        <p class="text-surface-6 text-base">Contact the team owner at <a class="text-surface-6 underline"
            :href="'mailto:' + userEmail">{{ userEmail }}</a> to gain access.</p>
      </section>

      <section v-else>
        <p class="text-s-s text-dark-7 text-normal mb-4">
          Create a token to log in and connect with vMix securely.
        </p>
        <p class="flex mb-6">
          <Tooltip :active="vmixTokens.length > 0"
            content="Only one token access can be created at a time. Please delete the existing token access to generate a new one.">
            <FormButton @click="createToken" :disabled="vmixTokens.length > 0">Create token
            </FormButton>
          </Tooltip>
        </p>
        <div v-if="processing" class="text-center py-8">
          <i class="fa fa-spinner fa-spin"></i>
        </div>
        <div v-else-if="vmixTokens.length > 0" class="max-w-full% overflow-x-auto lg:overflow-x-visible">
          <table class="w-full" :busy="loading">
            <thead class="!border-b !border-b-surface-3">
              <th class="font-normal text-left text-sm text-surface-6 py-2" v-for="(field, index) in fields" :key="index">
                {{ field.label }}
              </th>
            </thead>
            <tbody class="divide-y divide-surface-3 text-sm text-surface-9">
              <tr v-for="token in vmixTokens" :key="token._id || token.id">
                <td class="py-2">
                  {{ token.name }}
                </td>
                <td class="py-2">
                  {{ formatDate(token.createdAt) }}
                </td>
                <td class="text-right py-2">
                  <div class="flex gap-2 items-center justify-end">
                    <FormButton @click="editVmixToken(token)" size="sm" type="secondary">
                      Edit
                    </FormButton>
                    <FormButton @click="deleteVmixToken(token)" isIcon size="sm" type="danger">
                      <IconBase>
                        <IconBin />
                      </IconBase>
                    </FormButton>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </main>
  </div>
  <create-vmix-token-modal @token-created="onTokenUpdate" />
  <edit-vmix-token-modal :vmix-token="vmixToken" @token-dismiss="onTokenDismiss" @token-edited="onTokenUpdate" />
  <delete-vmix-token-modal :vmix-token="vmixToken" @token-deleted="onTokenUpdate" />
</template>

<style scoped></style>

